import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Badge, Button, Card, Col, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import Flex from '@/components/common/Flex';
import IconItem from '@/components/common/icon/IconItem';
import { useAppContext } from '@/Main';
import { NoProject } from '../components/NoProject';
import { AiLabel, RtlMetric } from '../components/common/RtlMetric';
import {
  DashboardSourcesWidget,
  useTrackerAiSourcesLink,
} from '../components/dashboard/DashboardSourcesWidget';
import { DashboardTopBrandRow } from '../components/dashboard/DashboardTopBrandRow';
import { DashboardTopBrandsHeader } from '../components/dashboard/DashboardTopBrandsHeader';
import { TopCompetitorsShareOfVoiceCard } from '../components/dashboard/TopCompetitorsShareOfVoiceCard';
import { TopCompetitorsByEngineCard } from '../components/dashboard/TopCompetitorsByEngineCard';
import { DashboardRecentPromptsTable } from '../components/dashboard/DashboardRecentPromptsTable';
import { BrandFavicon } from '../components/BrandFavicon';
import { OwnBrandOverviewCard } from '../components/dashboard/OwnBrandOverviewCard';
import { ModelBrandComparisonCard } from '../components/dashboard/ModelBrandComparisonCard';
import { PromptGapCard } from '../components/dashboard/competition/PromptGapCard';
import { RankDistributionCard } from '../components/dashboard/competition/RankDistributionCard';
import { ShareOfVoiceTrendCard } from '../components/dashboard/competition/ShareOfVoiceTrendCard';
import { TopicShareHeatmapCard } from '../components/dashboard/competition/TopicShareHeatmapCard';
import { ViewAllCompetitorsFooter } from '../components/dashboard/competition/ViewAllCompetitorsFooter';
import { Modal } from '../components/Modals';
import { DashboardPageSkeleton } from '../components/common/Skeleton';
import { KpiStatCard } from '../components/common/KpiStatCard';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import { ExecutionAlert } from '../components/executions/DuplicateExecutionNotice';
import { PageFilterBar } from '../components/filters/PageFilterBar';
import { useProject } from '../context/ProjectContext';
import { useTrackerAiPath } from '../utils/paths';
import { useProjectCompetitors } from '../hooks/useProjectCompetitors';
import { useProjectEverExecuted } from '../hooks/useProjectEverExecuted';
import { getHelpHtml } from '../utils/help/help-content';
import {
  computeVisibilityGapVsYou,
  findOwnBrandRow,
  formatCompetitorGapLabel,
  formatProjectVisibilityDelta,
  formatPromptCoverageValue,
} from '../utils/visibility-page-utils';
import { resolveAiMentions } from '../utils/visibility-metrics';
import { brandInDomainFilter, resolveVisibilityTopBrand } from '../utils/domain-filter-utils';
import { computePromptDomainMetrics, getPromptBrandRows } from '../utils/prompt-domain-metrics';
import type { ProjectVisibilitySummary } from '../types';
import {
  allEnteredCompetitors,
  canViewAllCompetitors,
  competitorBrandKey,
  DASHBOARD_COMPETITOR_PREVIEW,
  previewCompetitors,
} from '../utils/dashboard-competitors';
import { DashboardEmptyNote } from '../components/dashboard/DashboardEmptyNote';
import { DASHBOARD_PLOT_HEIGHT } from '../utils/competition-chart';

/** KPI card label: Persian title + help icon. */
function KpiLabel({ title, helpKey }: { title: string; helpKey: string }) {
  return (
    <span className="d-inline-flex align-items-center gap-1">
      {title}
      <HelpTooltip html={getHelpHtml(helpKey)} />
    </span>
  );
}

function WidgetSectionTitle({
  title,
  subtitle,
  icon,
  leading,
  action,
  className = 'mb-3 mt-1',
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  icon?: IconProp;
  leading?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  const {
    config: { themeVariant },
  } = useAppContext() as { config: { themeVariant?: string } };
  const lightPrimary = themeVariant === 'primary';
  const showLeading = leading ?? (icon ? (
    <IconItem
      tag="div"
      icon={icon}
      bg="primary"
      color={lightPrimary ? undefined : 'white'}
      size="sm"
      iconClass="fs--2"
      className="shadow-none flex-shrink-0"
      style={lightPrimary ? { color: 'var(--falcon-primary-text-emphasis)' } : undefined}
    />
  ) : null);

  return (
    <Flex className={`align-items-start justify-content-between gap-3 ${className}`}>
      <Flex className="align-items-start gap-2">
        {showLeading}
        <div className="d-flex flex-column">
          <h5 className="mb-0 text-700 lh-base">{title}</h5>
          {subtitle ? <p className="mb-0 fs--2 text-500">{subtitle}</p> : null}
        </div>
      </Flex>
      {action ? <div className="flex-shrink-0">{action}</div> : null}
    </Flex>
  );
}

export function DashboardPage() {
  return (
    <NoProject>
      <DashboardContent />
    </NoProject>
  );
}

type ExpandChart = 'sov' | 'model' | 'top' | 'gap' | 'topic' | 'topSov' | 'topEngine' | null;

const EXPAND_TITLES: Record<Exclude<ExpandChart, null>, string> = {
  sov: 'سهم از دیده‌شدن (Share of Voice)',
  model: 'مقایسه به تفکیک مدل AI',
  top: 'رقبای برتر',
  gap: 'شکاف پرامپت‌ها با رقبا',
  topic: 'سهم از دیده‌شدن به تفکیک موضوع',
  topSov: 'سهم از دیده‌شدن رقبای برتر',
  topEngine: 'سهم از دیده‌شدن رقبای برتر به تفکیک مدل',
};

function DashboardContent() {
  const { projectId, setNewPromptOpen, data, loading, loadFailed, error, domainFilterId } =
    useProject();
  const navigate = useNavigate();
  const toTrackerAi = useTrackerAiPath();
  const sourcesLink = useTrackerAiSourcesLink();
  const summary = data?.visibilitySummary ?? null;
  const [expand, setExpand] = useState<ExpandChart>(null);

  const { everExecuted, checking } = useProjectEverExecuted(
    projectId,
    summary?.summary.executions ?? 0,
    summary != null,
  );
  const hasPrompts = (data?.insight.counts.prompts ?? 0) > 0;
  const trackedCompetitors = useProjectCompetitors(projectId);
  const hasCompetitors =
    trackedCompetitors.length > 0 || (summary?.summary.competitorsTracked ?? 0) > 0;

  const ownBrand = summary?.ownBrand ?? null;
  const ownBrandRow = useMemo(
    () => (summary ? findOwnBrandRow(summary.topBrands) : null),
    [summary],
  );

  const topicNames = useMemo(
    () => new Map((data?.insight.topics ?? []).map((topic) => [topic.id, topic.name])),
    [data],
  );

  // Domain filter: comparison widgets keep the own brand plus the selected competitor only.
  const inDomainFilter = useCallback(
    (brand: {
      knownBrandId: string | null;
      canonicalName: string;
      relationship: string;
      domain?: string | null;
    }) =>
      brandInDomainFilter(
        {
          brandId: brand.knownBrandId,
          name: brand.canonicalName,
          domain: brand.domain,
          isOwn: brand.relationship === 'you',
        },
        domainFilterId,
        trackedCompetitors,
      ),
    [domainFilterId, trackedCompetitors],
  );
  const filteredTracked = useMemo(
    () =>
      domainFilterId === 'all'
        ? trackedCompetitors
        : trackedCompetitors.filter((item) => item.id === domainFilterId),
    [domainFilterId, trackedCompetitors],
  );
  const selectedCompetitor =
    domainFilterId !== 'all' && domainFilterId !== 'you'
      ? (trackedCompetitors.find((item) => item.id === domainFilterId) ?? null)
      : null;
  const focusBrand = useMemo(
    () =>
      summary && selectedCompetitor
        ? resolveVisibilityTopBrand(summary, domainFilterId, trackedCompetitors)
        : null,
    [summary, selectedCompetitor, domainFilterId, trackedCompetitors],
  );
  const focusChartBrand = selectedCompetitor
    ? {
        knownBrandId: focusBrand?.knownBrandId ?? null,
        canonicalName: focusBrand?.canonicalName ?? selectedCompetitor.brandName,
      }
    : null;
  const focusChartKey = focusChartBrand ? competitorBrandKey(focusChartBrand) : null;

  const topCompetitors = useMemo(
    () => (summary?.topCompetitors ?? []).filter(inDomainFilter),
    [summary, inDomainFilter],
  );
  const previewCompetitorsList = useMemo(
    () => previewCompetitors(topCompetitors),
    [topCompetitors],
  );
  const enteredCompetitors = useMemo(
    () => allEnteredCompetitors(topCompetitors, filteredTracked),
    [topCompetitors, filteredTracked],
  );
  const rangeHasExecutions = (summary?.summary.executions ?? 0) > 0;
  const showViewAll =
    domainFilterId === 'all' &&
    rangeHasExecutions &&
    canViewAllCompetitors(topCompetitors.length, trackedCompetitors.length);
  const previewKeys = useMemo(
    () => new Set(previewCompetitorsList.map((brand) => competitorBrandKey(brand))),
    [previewCompetitorsList],
  );
  const viewAllFooter = (chart: Exclude<ExpandChart, null>) => (
    <ViewAllCompetitorsFooter visible={showViewAll} onClick={() => setExpand(chart)} />
  );

  // "برندهای برتر شناسایی‌شده": every detected brand, not just tracked competitors.
  const topDetectedBrands = useMemo(
    () =>
      (summary?.topBrands ?? []).filter((brand) =>
        domainFilterId === 'all' ? brand.relationship !== 'you' : inDomainFilter(brand),
      ),
    [summary, domainFilterId, inDomainFilter],
  );
  const topBrandsInFilter = useMemo(
    () => (summary?.topBrands ?? []).filter(inDomainFilter),
    [summary, inDomainFilter],
  );
  const rankDistribution = useMemo(
    () => (summary?.rankDistribution ?? []).filter(inDomainFilter),
    [summary, inDomainFilter],
  );
  const topDetectedBrandsPreview = useMemo(
    () => previewCompetitors(topDetectedBrands),
    [topDetectedBrands],
  );
  const showViewAllTopBrands =
    rangeHasExecutions && topDetectedBrands.length > DASHBOARD_COMPETITOR_PREVIEW;
  const viewAllTopBrandsFooter = (chart: Exclude<ExpandChart, null>) => (
    <ViewAllCompetitorsFooter visible={showViewAllTopBrands} onClick={() => setExpand(chart)} />
  );

  const topDetectedBrandsCapped = useMemo(
    () => topDetectedBrands.slice(0, 20),
    [topDetectedBrands],
  );

  // Engine-breakdown modal: only brands with a scored engine, capped at 20.
  const topBrandsWithEngineData = useMemo(() => {
    const keysWithData = new Set(
      (summary?.topBrandsByModel ?? []).flatMap((model) =>
        model.brands
          .filter((brand) => brand.shareOfVoice > 0)
          .map((brand) => competitorBrandKey(brand)),
      ),
    );
    return topDetectedBrands
      .filter((brand) => keysWithData.has(competitorBrandKey(brand)))
      .slice(0, 20);
  }, [topDetectedBrands, summary]);

  const recentPrompts = useMemo(
    () =>
      (summary?.promptPerformance ?? [])
        .filter((item) => item.status === 'executed' || item.status === 'partial')
        .slice(0, 6)
        .map((item) => {
          if (domainFilterId === 'all') return item;
          const metrics = computePromptDomainMetrics(
            getPromptBrandRows(item),
            domainFilterId,
            trackedCompetitors,
          );
          return { ...item, visibilityScore: metrics.visibility ?? 0, avgPosition: metrics.avgPosition };
        }),
    [summary, domainFilterId, trackedCompetitors],
  );

  // KPI row describes the selected competitor when one is filtered, otherwise the own brand.
  const kpiSubject = selectedCompetitor?.brandName ?? 'برند شما';
  const kpi = selectedCompetitor
    ? {
        visibilityScore: focusBrand?.visibilityScore ?? null,
        shareOfVoice: focusBrand?.shareOfVoice ?? null,
        aiMentions: focusBrand ? resolveAiMentions(focusBrand) : null,
        promptCoverage: focusBrand?.promptCoverage ?? null,
        avgPosition: focusBrand?.avgPosition ?? null,
      }
    : {
        visibilityScore: summary?.summary.visibilityScore ?? null,
        shareOfVoice: ownBrand?.shareOfVoice ?? null,
        aiMentions: ownBrand?.aiMentions ?? 0,
        promptCoverage: ownBrand?.promptCoverage ?? null,
        avgPosition: ownBrand?.avgPosition ?? null,
      };

  // No summary yet means the range request has not settled. An empty onboarding
  // card here is the same flash as the other tabs.
  if (!loadFailed && (loading || checking || !summary)) {
    return (
      <div>
        <div className="mb-4">
          <PageFilterBar showSources splitDateAndFilters dateVariant="quick" />
        </div>
        <DashboardPageSkeleton />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4">
        <PageFilterBar showSources splitDateAndFilters dateVariant="quick" />
      </div>

      {error && <ExecutionAlert message={error} />}

      {!summary || !everExecuted ? (
        <OnboardingState
          hasPrompts={hasPrompts}
          hasCompetitors={hasCompetitors}
          onCreatePrompt={() => setNewPromptOpen(true)}
          onAddCompetitor={() => navigate(toTrackerAi('competitors'))}
        />
      ) : (
        <>
          <WidgetSectionTitle
            leading={
              selectedCompetitor ? (
                <BrandFavicon
                  name={selectedCompetitor.brandName}
                  host={selectedCompetitor.host}
                  size="sm"
                  plain
                />
              ) : (
                <BrandFavicon
                  name={ownBrand?.canonicalName ?? 'برند شما'}
                  domain={ownBrandRow?.domain}
                  faviconUrl={ownBrandRow?.faviconUrl}
                  relationship="you"
                  size="sm"
                  plain
                />
              )
            }
            title={
              <span className="d-inline-flex align-items-center gap-1">
                {kpiSubject}
                <HelpTooltip html={getHelpHtml('sections.your_brand')} />
              </span>
            }
          />
          <Row className="g-3 mb-4">
            <Col xs={12} sm={6} md={6} lg>
              <KpiStatCard
                imageIndex={0}
                label={<KpiLabel title="دیده‌شدن در AI" helpKey="ai_visibility" />}
                valueAnnotation="(AI Visibility)"
                value={kpi.visibilityScore != null ? `${kpi.visibilityScore}%` : '—'}
                valueClassName="text-warning"
                badgeText={
                  !selectedCompetitor && summary!.summary.visibilityScoreDelta != null
                    ? `${summary!.summary.visibilityScoreDelta > 0 ? '+' : ''}${summary!.summary.visibilityScoreDelta}%`
                    : undefined
                }
                badgeBg={
                  summary!.summary.visibilityScoreDelta == null
                    ? 'secondary'
                    : summary!.summary.visibilityScoreDelta > 0
                      ? 'success'
                      : summary!.summary.visibilityScoreDelta < 0
                        ? 'danger'
                        : 'secondary'
                }
                subtext={
                  selectedCompetitor
                    ? `برای ${kpiSubject}`
                    : formatProjectVisibilityDelta(summary!.summary.visibilityScoreDelta)
                }
              />
            </Col>
            <Col xs={12} sm={6} md={6} lg>
              <KpiStatCard
                imageIndex={1}
                label={<KpiLabel title="سهم از دیده‌شدن" helpKey="share_of_voice" />}
                valueAnnotation="(Share of Voice)"
                value={kpi.shareOfVoice != null ? `${kpi.shareOfVoice}%` : '—'}
                valueClassName="text-success"
                subtext={`سهم ${kpiSubject} از منشن همه برندها`}
              />
            </Col>
            <Col xs={12} sm={6} md={6} lg>
              <KpiStatCard
                imageIndex={2}
                label={
                  <span className="d-inline-flex align-items-center gap-1">
                    منشن در AI
                    <HelpTooltip html={getHelpHtml('ai_mentions')} />
                  </span>
                }
                value={kpi.aiMentions ?? '—'}
                valueClassName="text-info"
                subtext={`پاسخ‌های AI که ${kpiSubject} در آن‌ها آمد`}
              />
            </Col>
            <Col xs={12} sm={6} md={6} lg>
              <KpiStatCard
                imageIndex={3}
                label={<KpiLabel title="پوشش پرامپت" helpKey="prompt_coverage" />}
                valueAnnotation="(Prompt Coverage)"
                value={formatPromptCoverageValue(
                  kpi.promptCoverage,
                  summary!.summary.executedPrompts,
                  summary!.summary.prompts,
                )}
                subtext={`پرامپت‌هایی که ${kpiSubject} در آن‌ها ظاهر شد`}
              />
            </Col>
            <Col xs={12} sm={6} md={6} lg>
              <KpiStatCard
                imageIndex={4}
                label={
                  <span className="d-inline-flex align-items-center gap-1">
                    میانگین رتبه AI
                    <HelpTooltip html={getHelpHtml('avg_ai_position')} />
                  </span>
                }
                value={kpi.avgPosition != null ? `#${kpi.avgPosition}` : '—'}
                valueClassName="text-primary"
                subtext={`میانگین رتبه ${kpiSubject} در پاسخ‌های AI`}
              />
            </Col>
          </Row>

          <WidgetSectionTitle
            icon="trophy"
            title={
              <span className="d-inline-flex align-items-center gap-1">
                برند شما و رقبا
                <HelpTooltip html={getHelpHtml('top_competitors')} />
              </span>
            }
            subtitle="وضعیت برند شما در مقایسه با رقبای اصلی"
            action={
              <Button
                variant="link"
                size="sm"
                className="p-0 text-primary"
                onClick={() => navigate(toTrackerAi('competitors'))}
              >
                مدیریت رقبا ←
              </Button>
            }
          />
          <Row className="g-3 mb-3">
            <Col lg={4}>
              <OwnBrandOverviewCard
                ownBrand={ownBrand}
                competitors={previewCompetitorsList}
                trackedCompetitors={selectedCompetitor ? filteredTracked : []}
                footer={viewAllFooter('sov')}
                rangeHasExecutions={rangeHasExecutions}
                hasBrandAliases={(data?.insight.brandAliases.length ?? 0) > 0}
                focusKey={focusChartKey}
              />
            </Col>
            <Col lg={4}>
              <ModelBrandComparisonCard
                models={summary!.brandsByModel ?? []}
                competitors={previewCompetitorsList}
                footer={viewAllFooter('model')}
                focusBrand={focusChartBrand}
              />
            </Col>
            <Col lg={4}>
              <RankDistributionCard
                brands={rankDistribution}
                focusKey={focusChartKey}
                focusName={selectedCompetitor?.brandName}
              />
            </Col>
          </Row>
          <Row className="g-3 mb-3">
            <Col lg={6}>
              <TopCompetitorsCard
                competitors={previewCompetitorsList}
                competitorsTracked={summary!.summary.competitorsTracked}
                ownVisibilityScore={ownBrand?.visibilityScore ?? null}
                footer={viewAllFooter('top')}
              />
            </Col>
            <Col lg={6}>
              <PromptGapCard
                prompts={summary!.promptPerformance}
                competitors={previewCompetitorsList}
                footer={viewAllFooter('gap')}
              />
            </Col>
          </Row>
          <Row className="g-3 mb-4">
            <Col lg={6}>
              <ShareOfVoiceTrendCard />
            </Col>
            <Col lg={6}>
              <TopicShareHeatmapCard
                topics={summary!.brandsByTopic ?? []}
                topicNames={topicNames}
                previewKeys={previewKeys}
                footer={viewAllFooter('topic')}
                focusKey={focusChartKey}
              />
            </Col>
          </Row>

          <WidgetSectionTitle
            icon="crown"
            title={
              <span className="d-inline-flex align-items-center gap-1">
                برندهای برتر شناسایی‌شده
                <HelpTooltip html={getHelpHtml('top_detected_brands')} />
              </span>
            }
            subtitle="بیشترین دیده‌شدن در این بازه"
            action={
              <Button
                variant="link"
                size="sm"
                className="p-0 text-decoration-none"
                style={{ color: 'var(--falcon-navbar-vertical-default-link-active-color)' }}
                onClick={() => navigate(toTrackerAi('visibility'))}
              >
                مشاهده همه ←
              </Button>
            }
          />
          <Row className="g-3 mb-3">
            <Col lg={6}>
              <TopCompetitorsShareOfVoiceCard
                competitors={topDetectedBrandsPreview}
                footer={viewAllTopBrandsFooter('topSov')}
              />
            </Col>
            <Col lg={6}>
              <TopCompetitorsByEngineCard
                models={summary!.topBrandsByModel ?? []}
                competitors={topDetectedBrandsPreview}
                footer={viewAllTopBrandsFooter('topEngine')}
              />
            </Col>
          </Row>
          <Card className="shadow-sm mb-4">
            <Card.Body className="pt-3">
              {topBrandsInFilter.length === 0 ? (
                <DashboardEmptyNote minHeight={160}>
                  در این بازه برندی شناسایی نشده.
                </DashboardEmptyNote>
              ) : (
                <>
                  <DashboardTopBrandsHeader />
                  <ul className="list-unstyled d-flex flex-column gap-2 mb-0">
                    {topBrandsInFilter.slice(0, 5).map((brand, index) => (
                      <DashboardTopBrandRow
                        key={brand.knownBrandId ?? brand.canonicalName}
                        brand={brand}
                        rank={index + 1}
                        totalExecutions={summary!.summary.executions}
                      />
                    ))}
                  </ul>
                </>
              )}
            </Card.Body>
          </Card>

          <WidgetSectionTitle
            icon="link"
            title="منابع AI"
            subtitle="دامنه‌هایی که در پاسخ‌های AI به عنوان منبع دیده شده‌اند"
            action={
              <Link to={sourcesLink} className="fs--2 fw-medium text-decoration-none" style={{ color: 'var(--falcon-navbar-vertical-default-link-active-color)' }}>
                مشاهده همه منابع ←
              </Link>
            }
          />
          <div className="mb-4">
            <DashboardSourcesWidget hideHeader />
          </div>

          <WidgetSectionTitle
            icon="list-alt"
            title={
              <span className="d-inline-flex align-items-center gap-1">
                عملکرد اخیر پرامپت
                <HelpTooltip html={getHelpHtml('recent_prompt_performance')} />
              </span>
            }
            subtitle="آخرین پرامپت‌های اجراشده"
            action={
              <Link to={toTrackerAi('prompts')} className="fs--2 fw-medium text-primary">
                همه پرامپت‌ها ←
              </Link>
            }
          />
          <DashboardRecentPromptsTable
            prompts={recentPrompts}
            positionLabel={
              domainFilterId === 'all' || domainFilterId === 'you'
                ? 'رتبه میانگین برند ما'
                : `رتبه میانگین ${selectedCompetitor?.brandName ?? 'برند انتخاب‌شده'}`
            }
          />

          <Modal
            open={expand != null}
            onClose={() => setExpand(null)}
            title={expand ? EXPAND_TITLES[expand] : ''}
            subtitle="همه رقبای واردشده"
            wide
            scrollable
            dialogClassName="modal-xl"
          >
            {expand === 'sov' && (ownBrand?.canonicalName || focusChartKey) ? (
              <OwnBrandOverviewCard
                ownBrand={ownBrand}
                competitors={enteredCompetitors}
                trackedCompetitors={filteredTracked}
                hideHeader
                plotHeight={Math.max(
                  DASHBOARD_PLOT_HEIGHT,
                  (enteredCompetitors.length + 1) * 28 + 56,
                )}
                focusKey={focusChartKey}
              />
            ) : null}
            {expand === 'model' ? (
              <ModelBrandComparisonCard
                models={summary!.brandsByModel ?? []}
                competitors={enteredCompetitors}
                hideHeader
                plotHeight={Math.max(
                  DASHBOARD_PLOT_HEIGHT,
                  enteredCompetitors.length * 28 + 80,
                )}
                focusBrand={focusChartBrand}
              />
            ) : null}
            {expand === 'top' ? (
              <TopCompetitorsCard
                competitors={enteredCompetitors}
                competitorsTracked={Math.max(
                  summary!.summary.competitorsTracked,
                  trackedCompetitors.length,
                )}
                ownVisibilityScore={ownBrand?.visibilityScore ?? null}
                hideHeader
              />
            ) : null}
            {expand === 'gap' ? (
              <PromptGapCard
                prompts={summary!.promptPerformance}
                competitors={enteredCompetitors}
                hideHeader
              />
            ) : null}
            {expand === 'topic' ? (
              <TopicShareHeatmapCard
                topics={summary!.brandsByTopic ?? []}
                topicNames={topicNames}
                hideHeader
                enteredBrands={enteredCompetitors}
                focusKey={focusChartKey}
              />
            ) : null}
            {expand === 'topSov' ? (
              <TopCompetitorsShareOfVoiceCard
                competitors={topDetectedBrandsCapped}
                hideHeader
                plotHeight={Math.max(
                  DASHBOARD_PLOT_HEIGHT,
                  topDetectedBrandsCapped.length * 28 + 56,
                )}
              />
            ) : null}
            {expand === 'topEngine' ? (
              <TopCompetitorsByEngineCard
                models={summary!.topBrandsByModel ?? []}
                competitors={topBrandsWithEngineData}
                hideHeader
                plotHeight={Math.max(
                  DASHBOARD_PLOT_HEIGHT,
                  topBrandsWithEngineData.length * 28 + 56,
                )}
              />
            ) : null}
          </Modal>
        </>
      )}
    </div>
  );
}

function TopCompetitorsCard({
  competitors,
  competitorsTracked,
  ownVisibilityScore,
  footer,
  hideHeader,
}: {
  competitors: ProjectVisibilitySummary['topCompetitors'];
  competitorsTracked: number;
  ownVisibilityScore: number | null;
  footer?: ReactNode;
  hideHeader?: boolean;
}) {
  return (
    <Card className="h-100 shadow-sm d-flex flex-column">
      {hideHeader ? null : (
        <FalconCardHeader
          light
          titleTag="h6"
          className="py-3"
          title={
            <div>
              <span className="d-inline-flex align-items-center gap-1">
                رقبای برتر
                <HelpTooltip html={getHelpHtml('top_competitors')} />
              </span>
              <p className="fs--2 text-700 mb-0 fw-normal mt-2">شکاف دیده‌شدن نسبت به برند شما</p>
            </div>
          }
        />
      )}
      <Card.Body className="flex-grow-1 d-flex flex-column">
        {competitorsTracked === 0 ? (
          <DashboardEmptyNote>هنوز رقیبی اضافه نشده.</DashboardEmptyNote>
        ) : competitors.length === 0 ? (
          <DashboardEmptyNote>رقبای ردیابی‌شده در این بازه دیده نشدند.</DashboardEmptyNote>
        ) : (
          <ul className="list-unstyled mb-0">
            {competitors.map((row) => {
              const gap = computeVisibilityGapVsYou(ownVisibilityScore, row.visibilityScore);
              const gapLabel = formatCompetitorGapLabel(gap, row.canonicalName);
              const gapClass =
                gapLabel.tone === 'ahead'
                  ? 'text-success'
                  : gapLabel.tone === 'behind'
                    ? 'text-warning'
                    : 'text-500';

              return (
                <li
                  key={row.knownBrandId ?? row.canonicalName}
                  className="d-flex align-items-center justify-content-between gap-3 rounded-3 border bg-body-tertiary px-3 py-2 mb-2"
                >
                  <div className="min-w-0">
                    <p className="text-truncate fs--1 fw-medium text-1000 mb-0">
                      {row.canonicalName}
                    </p>
                    <p className="mt-1 d-flex flex-wrap align-items-center gap-2 fs--2 text-500 mb-0">
                      <RtlMetric
                        value={row.avgPosition != null ? `#${row.avgPosition}` : '—'}
                        label="میانگین رتبه"
                      />
                      <span className="text-300" aria-hidden>
                        ·
                      </span>
                      <RtlMetric
                        value={resolveAiMentions(row)}
                        label={<AiLabel prefix="منشن در" />}
                      />
                    </p>
                  </div>
                  <div className="d-flex flex-shrink-0 align-items-center gap-3">
                    <p
                      className="fs--1 fw-semibold text-800 mb-0"
                      style={{ fontVariantNumeric: 'tabular-nums' }}
                    >
                      {row.visibilityScore}%
                    </p>
                    <p
                      className={`text-end fs--2 fw-medium mb-0 ${gapClass}`}
                      style={{ minWidth: '7.5rem', fontVariantNumeric: 'tabular-nums' }}
                    >
                      {gapLabel.text}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card.Body>
      {footer}
    </Card>
  );
}

function OnboardingState({
  hasPrompts,
  hasCompetitors,
  onCreatePrompt,
  onAddCompetitor,
}: {
  hasPrompts: boolean;
  hasCompetitors: boolean;
  onCreatePrompt: () => void;
  onAddCompetitor: () => void;
}) {
  const {
    config: { themeVariant },
  } = useAppContext() as { config: { themeVariant?: string } };
  const lightPrimary = themeVariant === 'primary';

  return (
    <Card className="border-dashed shadow-sm">
      <Card.Body className="px-4 py-5">
        <div className="mx-auto text-center" style={{ maxWidth: '32rem' }}>
          <div
            className={`mx-auto mb-3 d-flex align-items-center justify-content-center rounded-3 bg-primary ${lightPrimary ? '' : 'text-white'}`}
            style={{
              width: '3rem',
              height: '3rem',
              ...(lightPrimary
                ? { color: 'var(--falcon-primary-text-emphasis)' }
                : null)
            }}
          >
            <FontAwesomeIcon icon="chart-line" size="lg" />
          </div>
          <h2 className="fs-1 fw-semibold text-1000">شروع ردیابی دیده‌شدن در AI</h2>
          <p className="mx-auto mt-2 fs--1 text-500 mb-0">
            دامنه شما ثبت شده است. برای دیدن بینش‌ها، رقیب و پرامپت را اضافه کنید.
          </p>
        </div>

        <div className="mx-auto mt-4 d-flex flex-column gap-3" style={{ maxWidth: '30rem' }}>
          <div className="d-flex align-items-start gap-3 rounded-3 border bg-body-tertiary px-3 py-3">
            <Badge
              bg={hasCompetitors ? 'success' : 'primary'}
              pill
              className="mt-1 flex-shrink-0 d-flex align-items-center justify-content-center"
              style={{ width: '1.5rem', height: '1.5rem' }}
            >
              {hasCompetitors ? '✓' : '1'}
            </Badge>
            <div className="flex-grow-1 min-w-0">
              <p className="fs--1 fw-medium text-1000 mb-0">افزودن رقیب</p>
              <p className="mt-1 fs--2 text-500 mb-0">
                رقبای اصلی را اضافه کنید تا دیده‌شدن برندتان با آن‌ها مقایسه شود.
              </p>
              {!hasCompetitors && (
                <Button variant="primary" size="sm" className="mt-2" onClick={onAddCompetitor}>
                  افزودن رقیب
                </Button>
              )}
            </div>
          </div>
          <div className="d-flex align-items-start gap-3 rounded-3 border bg-body-tertiary px-3 py-3">
            <Badge
              bg={hasPrompts ? 'success' : 'primary'}
              pill
              className="mt-1 flex-shrink-0 d-flex align-items-center justify-content-center"
              style={{ width: '1.5rem', height: '1.5rem' }}
            >
              {hasPrompts ? '✓' : '2'}
            </Badge>
            <div className="flex-grow-1 min-w-0">
              <p className="fs--1 fw-medium text-1000 mb-0">افزودن پرامپت</p>
              <p className="mt-1 fs--2 text-500 mb-0">
                سوال‌هایی که کاربران از AI می‌پرسند را وارد کنید.
              </p>
              {!hasPrompts && (
                <Button variant="primary" size="sm" className="mt-2" onClick={onCreatePrompt}>
                  ایجاد پرامپت
                </Button>
              )}
            </div>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
}

export default DashboardPage;
